import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { logError } from "@/lib/log";

const COLORS = ["#FDE68A", "#FBCFE8", "#C7D2FE", "#BBF7D0", "#FED7AA", "#DDD6FE"];
const IDEAS = ["I'm grateful you're my friend", "Thank you for always making me laugh", "You make hard days lighter"];

export const SupporterGratitudeForm = ({ token, name }) => {
  const [text, setText] = useState("");
  const [color, setColor] = useState(COLORS[1]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState([]);

  const load = useCallback(() => api.get(`/supporters/view/${token}/gratitude`).then((r) => setSent(r.data.sent)).catch((e) => logError("Gratitude sent failed:", e)), [token]);
  useEffect(() => { load(); }, [load]);

  const drop = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/supporters/view/${token}/gratitude`, { text: text.trim(), color }); setText(""); toast.success(`Dropped into ${name}'s jar 🫙`); load(); }
    catch (err) { toast.error(errMsg(err, "Could not add your note")); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={drop} className="mm-card p-6" data-testid="supporter-gratitude-form">
      <h2 className="font-semibold">🫙 Drop a gratitude note in {name}'s jar</h2>
      <p className="text-sm text-mm-muted mt-1">Share something you're grateful for about {name}. They can find it again when they shake their jar on a hard day.</p>
      <div className="flex flex-wrap gap-2 mt-3">
        {IDEAS.map((t, i) => <button type="button" key={t} className="mm-chip !text-xs" onClick={() => setText(t)} data-testid={`supporter-gratitude-idea-${i}`}>{t}</button>)}
      </div>
      <textarea className="mm-input min-h-[80px] mt-3 !text-[#3A2E4F] placeholder:!text-[#3A2E4F]/70" maxLength={280} value={text} onChange={(e) => setText(e.target.value)} placeholder={`What are you grateful for about ${name}?`} style={{ background: color }} data-testid="supporter-gratitude-input" />
      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
        <div className="flex gap-2">
          {COLORS.map((c) => <button type="button" key={c} onClick={() => setColor(c)} className={`w-7 h-7 rounded-full border-2 ${color === c ? "border-mm-primary scale-110" : "border-white"} transition-transform`} style={{ background: c }} aria-label={`Colour ${c}`} data-testid={`supporter-gratitude-color-${c.slice(1)}`} />)}
        </div>
        <button className="mm-btn" disabled={busy || !text.trim()} data-testid="supporter-gratitude-send">{busy && <Loader2 size={16} className="animate-spin" />}Add to their jar</button>
      </div>
      {sent.length > 0 && (
        <div className="mt-4" data-testid="supporter-gratitude-sent">
          <div className="text-xs font-semibold text-mm-muted mb-2">Notes you've added</div>
          <div className="flex flex-wrap gap-2">
            {sent.map((n) => <span key={n.id} className="rounded-xl px-3 py-1.5 text-xs text-[#3A2E4F] max-w-full break-words" style={{ background: n.color }} data-testid={`supporter-gratitude-sent-${n.id}`}>{n.text}{n.hearted && <span className="font-semibold text-[#B23A63]" data-testid={`supporter-gratitude-loved-${n.id}`}> · 💜 {name} loved this</span>}</span>)}
          </div>
        </div>
      )}
    </form>
  );
};
